import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { DeleteFolderEntitiesRequest, DeleteFolderEntitiesResponse, EntityType } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

export const useDeleteFolderEntities = (authToken: string) => {
  const { workspaceId } = useParams();
  const queryClient = useQueryClient();

  return useMutation<DeleteFolderEntitiesResponse, Error, DeleteFolderEntitiesRequest>({
    mutationFn: async (request: DeleteFolderEntitiesRequest) => {
      const response = await fetch(ENDPOINTS.folder_entities(String(workspaceId)), {
        method: "DELETE",
        headers: jsonAuthHeaders(authToken),
        body: JSON.stringify({ ...request, cascade: true }),
      });
      const data: DeleteFolderEntitiesResponse = await handleResponse(response);

      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate changelog queries for each deleted entity
      variables.entities.forEach(entity => {
        queryClient.invalidateQueries({
          queryKey: ["changelog", workspaceId, entity.id, entity.type],
        });
      });
      // Invalidate global changelog
      queryClient.invalidateQueries({
        queryKey: ["global-changelog", workspaceId],
      });
      // Invalidate all folder-entities queries since we don't know which folders were affected
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId],
      });

      // Favorites for deleted entities (incl. nested folders on cascade) are cleaned server-side
      queryClient.invalidateQueries({
        queryKey: ["favorites", Number(workspaceId)],
      });
      queryClient.invalidateQueries({
        queryKey: ["favorite-stars"],
      });

      // If there are specific entity types, we could also invalidate their specific queries
      const entityTypes = new Set(variables.entities.map(e => e.type));

      // Invalidate prompt queries if prompts were deleted
      if (entityTypes.has(EntityType.PROMPT)) {
        queryClient.invalidateQueries({
          queryKey: ["prompts"],
        });
      }

      // Invalidate workflow queries if workflows were deleted
      if (entityTypes.has(EntityType.WORKFLOW)) {
        queryClient.invalidateQueries({
          queryKey: ["workflows"],
        });
      }

      // Invalidate dataset queries if datasets were deleted
      if (entityTypes.has(EntityType.DATASET)) {
        queryClient.invalidateQueries({
          queryKey: ["datasets"],
        });
      }

      // Invalidate report queries if reports were deleted
      if (entityTypes.has(EntityType.REPORT)) {
        queryClient.invalidateQueries({
          queryKey: ["reports"],
        });
      }

      // Invalidate A/B test queries if A/B tests were deleted
      if (entityTypes.has(EntityType.AB_TEST)) {
        queryClient.invalidateQueries({
          queryKey: ["ab-tests"],
        });
      }

      // Invalidate input variable set queries if input variable sets were deleted
      if (entityTypes.has(EntityType.INPUT_VARIABLE_SET)) {
        queryClient.invalidateQueries({
          queryKey: ["input-variable-sets"],
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      // Handle specific error messages from the API
      let errorMessage = "Failed to delete entities";

      if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;

        // Provide more user-friendly messages for common errors
        if (error.message.includes("not empty")) {
          errorMessage =
            "Cannot delete folder: It still contains items. Please delete or move the contents first.";
        } else if (error.message.includes("Could not find")) {
          errorMessage =
            "One or more items could not be found. They may have already been deleted.";
        } else if (error.message.includes("Invalid entity_type")) {
          errorMessage = "Invalid item type specified";
        } else if (error.message.includes("Invalid workspace_id")) {
          errorMessage = "Invalid workspace. Please refresh and try again.";
        }
      }

      displayErrorToast(errorMessage);
    },
  });
};
